import { compact } from 'lodash';
import { useMemo } from 'react';

import { TypeaheadListInputOption } from '@/components/form/formAwareInputs/FormAwareTypeaheadListInput';
import { HouseholdManagementSource } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';
import { sortAlpha } from '@/utils/sortUtils';

import {
  ManageClientAccessForm_CollaboratorFragment,
  ManageClientAccessForm_EmployeeFragment,
  useManageClientAccessFormQuery,
} from './graphql/ManageHouseholdAccessForm.generated';
import { ManageHouseholdAccessFormShape } from './ManageHouseholdAccessForm.types';

function getCollaboratorListOptionFromCollaborator(
  collaborator: ManageClientAccessForm_CollaboratorFragment
): TypeaheadListInputOption {
  return {
    id: collaborator.id,
    heading: collaborator.displayName,
    description: collaborator.organizationName ?? undefined,
    additionalItems: compact([collaborator.title]),
  };
}

function getAssignedEmployeeListOptionFromEmployee(
  employee: ManageClientAccessForm_EmployeeFragment,
  relationshipOwnerId: string | null
): TypeaheadListInputOption {
  return {
    id: employee.user.id,
    heading: employee.user.displayName,
    description: employee.user.email,
    badgeText:
      employee.user.id === relationshipOwnerId
        ? 'Relationship owner'
        : undefined,
  };
}

const defaultValues: ManageHouseholdAccessFormShape = {
  collaborators: [],
  assignedUsers: [],
};

export function useHouseholdAccessFormData({
  householdId,
}: {
  householdId: string;
}) {
  const { data, ...queryProps } = useManageClientAccessFormQuery({
    variables: {
      householdId,
    },
    fetchPolicy: 'cache-and-network',
  });

  const household = (() => {
    if (data?.household?.__typename !== 'Household') return null;
    return data?.household;
  })();

  const householdType = household?.__typename;
  const collaborators = household?.collaborators;
  const assignedEmployees = household?.assignedEmployees;
  const relationshipOwnerId = household?.primaryRelationshipOwner.id;
  const formData: ManageHouseholdAccessFormShape = useMemo(() => {
    if (householdType !== 'Household') {
      return defaultValues;
    }

    return {
      collaborators: (collaborators ?? [])
        .map(getCollaboratorListOptionFromCollaborator)
        .sort((a, b) => sortAlpha(a.heading, b.heading)),
      assignedUsers: (assignedEmployees ?? [])
        .map((e) =>
          getAssignedEmployeeListOptionFromEmployee(
            e,
            relationshipOwnerId ?? null
          )
        )
        .sort((a, b) => sortAlpha(a.heading, b.heading)),
    };
  }, [householdType, assignedEmployees, collaborators, relationshipOwnerId]);

  // these are the collaborators for this tenant who are eligible to be associated with this client
  const collaboratorOptions = useMemo(() => {
    return getNodes(data?.collaborators).map(
      getCollaboratorListOptionFromCollaborator
    );
  }, [data]);

  const assignableEmployeeOptions = useMemo(
    () =>
      getNodes(data?.employees).map((e) =>
        getAssignedEmployeeListOptionFromEmployee(e, null)
      ),
    [data]
  );

  const userIdToEmployeeIdMap = useMemo(() => {
    const res: Record<string, string> = {};

    getNodes(data?.employees).forEach((employee) => {
      res[employee.user.id] = employee.id;
    });

    return res;
  }, [data]);

  return {
    formData,
    collaboratorOptions,
    assignableEmployeeOptions,
    userIdToEmployeeIdMap,
    isSnowflakeManaged:
      household?.householdManagementSource ===
      HouseholdManagementSource.Snowflake,
    relationshipOwnerId: household?.primaryRelationshipOwner.id ?? null,
    ...queryProps,
  };
}
